import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtils } from '../../shared/http-utils';
import { Observable } from 'rxjs';
import { URL_BASE } from '../../core/constants';
import { Trip } from './trip.model';
import { TripPingLight } from './trip-ping-light.model';
import { Carrier } from '../../user-dashboard/shared/carrier.model';
import { map } from 'rxjs/operators';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import { SortDirection } from '../../shared/sort-direction';
import { TripSearchCriteria } from './trip-search-criteria.model';
import { TripDateCriteria } from './trip-date-criteria';
import * as moment from 'moment'
import { Moment } from 'moment'
import { RefreshEtas } from './refresh-etas.model';
import { TripRefreshDistance } from './trip-refresh-distance.model';
import { TripLatng } from './trip-latng.model';
import { TripEvent } from './trip-event.model';
import { EtaReport } from './eta-report.model';
import { CustomEncoder } from '../../shared/custom-encoder';
import { Stats } from './stat.model';
import { Region } from '../../user-dashboard/shared/region.model';
import { TripSortableColumn } from './trip-sortable-column';
import { TripStatus } from './trip-status';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { DateTimezoneService } from './date-timezone.service';

@Injectable()
export class TripService {

  private TRIPS_URI = 'trips';

  public static prepareSearchRequest(
    criteria: TripSearchCriteria,
    page: number,
    size: number,
    sortColumn: TripSortableColumn,
    direction: SortDirection,
    statusOrder: TripStatus
  ) {
    for (const key of Object.keys(criteria)) {
      if (!criteria[key] && key !== 'exclude_future_loads') {
        delete criteria[key];
      }
    }
    if (criteria.departure_date && criteria.departure_date === TripDateCriteria.ALL) {
      delete criteria['before_departure_date'];
      delete criteria['after_departure_date'];
    }
    if (criteria.arrival_date && criteria.arrival_date === TripDateCriteria.ALL) {
      delete criteria['before_arrival_date'];
      delete criteria['after_arrival_date'];
    }

    const headers = HttpUtils.createHeaders();
    const params: any = {
      page: String(page),
      size: String(size),
      column: sortColumn,
      direction: direction,
      ...criteria,
      any_references: criteria.any_references ? criteria.any_references.split(',').map(ref => ref.trim()) : [],
    };

    if (sortColumn === TripSortableColumn.STATUS) {
      params.status_order = statusOrder;
    }
    return { headers, params };
  }

  constructor(
    private authService: AuthenticationService,
    private dateTimezoneService: DateTimezoneService,
    private http: HttpClient
  ) {}

  public searchWithAddresses(
    carrier: Carrier,
    region: Region,
    page: number,
    size: number,
    sortColumn: TripSortableColumn,
    direction: SortDirection,
    statusOrder: TripStatus,
    criteria: TripSearchCriteria
  ): Observable<SearchObjectResult<Trip>> {
    const { headers, params } = TripService.prepareSearchRequest(criteria, page, size, sortColumn, direction, statusOrder);
    const carrierUrl = this.authService.canManageCarrierTrips()
      ? 'self' : carrier
        ? carrier.id : region.id;
    const url = `${URL_BASE}/api/${this.TRIPS_URI}/search/by/${carrier ? 'carrier' : 'region'}/${carrierUrl}/criteria`;
    return this.http.get(url, { headers, params })
      .pipe(map(result => SearchObjectResult.fromDto(result, Trip.fromDto)));
  }

  public searchByDeliveryNumber(deliveryNumber: string, hash: string, serviceName: string): Observable<Trip> {
    const headers = HttpUtils.createHeaders();
    const params = {
      deliveryNumber: deliveryNumber,
      hash: hash,
      serviceName: serviceName
    };
    const httpParams = new HttpParams({
      encoder: new CustomEncoder(),
      fromObject: params
    });
    const url = `${URL_BASE}/api/integrations/${serviceName}/track-a-trip`;

    return this.http.get(url, { headers, params: httpParams })
      .pipe(
        map(result => Trip.fromDto(result))
      );
  }

  public getByCarrierAndLoadUuid(carrier: Carrier, loadUuid: string): Observable<Trip> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/by/carrier/${carrier.id}/and/${loadUuid}`;

    return this.http.get(url, { headers })
      .pipe(
        map(t => Trip.fromDto(t))
      );
  }

  public getBySelfCarrierAndLoadUuid(loadUuid: string): Observable<Trip> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/by/carrier/self/and/${loadUuid}`;

    return this.http.get(url, { headers })
      .pipe(
        map(t => Trip.fromDto(t))
      );
  }

  public getById(tripId: string): Observable<Trip> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/${tripId}`;

    return this.http.get(url, { headers })
      .pipe(
        map(t => Trip.fromDto(t))
      );
  }

  public getByCustomerId(customerId: string): Observable<Trip> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/by/customer-id/${customerId}`;

    return this.http.get(url, { headers })
      .pipe(
        map(t => Trip.fromDto(t))
      );
  }

  public getLightById(tripId: string): Observable<Trip> {
    const url = `${URL_BASE}/api/${this.TRIPS_URI}/${tripId}/light`;

    return this.http.get(url)
      .pipe(
        map(t => Trip.fromDto(t))
      );
  }

  public getLatestPing(tripId: string): Observable<TripPingLight> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/${tripId}/latest-ping`;

    return this.http.get(url, { headers })
      .pipe(
        map(p => TripPingLight.fromDto(p))
      );
  }

  public getTripEvents(tripId: string): Observable<TripEvent[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/${tripId}/events`;

    return this.http.get(url, { headers })
      .pipe(
        map((events: any[]) => events.map(e => TripEvent.fromDto(e)))
      );
  }

  public getTripEtas(deliveryNumbers: string[]): Observable<EtaReport[]> {
    const headers = HttpUtils.createHeaders();
    const params = { deliveryNumbers: deliveryNumbers };
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/etas`;

    return this.http.get(url, { headers, params })
      .pipe(
        map((etas: any[]) => etas.map(e => EtaReport.fromDto(e)))
      );
  }

  public sendCustomNotification(trip: Trip, title: string, message: string): Observable<any> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/${trip.tripId}/notifications/send/custom`;

    return this.http.post(url, { title, message }, { headers });
  }

  public getPingsLight(tripId: string, maxAccuracy: number = 100): Observable<TripPingLight[]> {
    const headers = HttpUtils.createHeaders();
    let params    = {};

    if (maxAccuracy !== null) {
      params = {
        max_accuracy: String(maxAccuracy)
      };
    }

    const url = `${URL_BASE}/api/${this.TRIPS_URI}/${tripId}/pings/light`;

    return this.http.get(url, { headers, params })
      .pipe(
        map((pings: any[]) => pings.map(p => TripPingLight.fromDto(p)))
      );
  }

  public close(trip: Trip): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.TRIPS_URI}/${trip.tripId}/close`;

    return this.http.post<void>(url, {}, { headers });
  }

  public fetchRefreshEta(trip: Trip, latestKnownEtasAt: Moment, singleTrip: boolean): Observable<RefreshEtas> {
    const headers = !singleTrip ? HttpUtils.createHeaders() : null;
    const param   = latestKnownEtasAt !== null ? `?latest_known_etas_at=${moment(latestKnownEtasAt).toISOString()}` : '' ;
    const baseUrl = `${URL_BASE}/api/${this.TRIPS_URI}`;
    const url = singleTrip ? `/by/customer-id/${trip.customerId}` : `/${trip.tripId}`;
    const urlWithParam = `${baseUrl}${url}/refresh-etas${param}`;

    return this.http.post<RefreshEtas>(urlWithParam, {}, { headers })
      .pipe(
        map((refreshEtas: any) => RefreshEtas.fromDto(refreshEtas))
      );
  }

  public fetchRefreshRemainingDistance(trip: Trip, latestKnowPingCreatedAt: Moment, singleTrip: boolean): Observable<TripRefreshDistance> {
    const headers = !singleTrip ? HttpUtils.createHeaders() : null;
    const baseUrl = `${URL_BASE}/api/${this.TRIPS_URI}`;
    const param   = latestKnowPingCreatedAt !== null ? `?latest_known_ping_created_at=${moment(latestKnowPingCreatedAt).toISOString()}` : '' ;
    const url = singleTrip ? `/by/customer-id/${trip.customerId}` : `/${trip.tripId}`;
    const urlWithParam = `${baseUrl}${url}/distance${param}`;

    return this.http.get<TripRefreshDistance>(urlWithParam, { headers })
      .pipe(
        map((refreshDistance: any) => TripRefreshDistance.fromDto(refreshDistance))
      );
  }

  public resolveAndGetTripOriginAndDestinationLatng(trip: Trip, singleTrip: boolean): Observable<TripLatng> {
    const headers = HttpUtils.createHeaders();
    const baseUrl = `${URL_BASE}/api/${this.TRIPS_URI}`;
    const url = singleTrip ? `/by/customer-id/${trip.customerId}` : `/${trip.tripId}`;
    const urlWithParam = `${baseUrl}${url}/latng`;

    return this.http.post<TripLatng>(urlWithParam, {}, { headers })
      .pipe(
        map((tripLatng: any) => TripLatng.fromDto(tripLatng))
      );
  }

  public getStatsByCarrier(carrier: Carrier): Observable<Stats> {
    const headers = HttpUtils.createHeaders();
    const baseUrl = `${URL_BASE}/api/${this.TRIPS_URI}`;
    const url = `/by/carrier/${carrier.id}`;
    const urlWithParam = `${baseUrl}${url}/stats?zoneId=${this.dateTimezoneService.selectedTimeZone.zone}`;

    return this.http.get(urlWithParam, { headers })
      .pipe(
        map((stat: any) => Stats.fromDto(stat))
      );
  }

  public getStatsByRegion(region: Region): Observable<Stats> {
    const headers = HttpUtils.createHeaders();
    const baseUrl = `${URL_BASE}/api/${this.TRIPS_URI}`;
    const url = `/by/region/${region.id}`;
    const urlWithParam = `${baseUrl}${url}/stats?zoneId=${this.dateTimezoneService.selectedTimeZone.zone}`;

    return this.http.get(urlWithParam, { headers })
      .pipe(
        map((stat: any) => Stats.fromDto(stat))
      );
  }
}
